/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  TrustedClientUserSessionIssuerReference,
  TrustedClientUserSessionIssuerReference$inboundSchema,
} from "./trustedclientusersessionissuerreference.js";

/**
 * Stable reason deletion is blocked. Present when can_delete is false.
 */
export const BlockingReason = {
  IdentityProviderLogin: "identity_provider_login",
  IdentityChaining: "identity_chaining",
} as const;
/**
 * Stable reason deletion is blocked. Present when can_delete is false.
 */
export type BlockingReason = OpenEnum<typeof BlockingReason>;

/**
 * Authoritative impact summary for deleting a remote_session_client, including identity-provider login references that block deletion.
 */
export type OrganizationClientDeletePreflight = {
  /**
   * Stable reason deletion is blocked. Present when can_delete is false.
   */
  blockingReason?: BlockingReason | undefined;
  /**
   * Whether the client can be deleted now.
   */
  canDelete: boolean;
  /**
   * Active identity-chaining bindings that must be explicitly unlinked before deletion.
   */
  emaBindingCount: number;
  /**
   * Display names of MCP servers this client is attached to.
   */
  mcpServerNames: Array<string>;
  /**
   * Number of non-deleted remote_sessions minted against this client.
   */
  sessionCount: number;
  /**
   * Organization-owned user-session issuers that use this client for identity-provider login and block deletion.
   */
  trustedUserSessionIssuers: Array<TrustedClientUserSessionIssuerReference>;
};

/** @internal */
export const BlockingReason$inboundSchema: z.ZodMiniType<
  BlockingReason,
  unknown
> = openEnums.inboundSchema(BlockingReason);

/** @internal */
export const OrganizationClientDeletePreflight$inboundSchema: z.ZodMiniType<
  OrganizationClientDeletePreflight,
  unknown
> = z.pipe(
  z.object({
    blocking_reason: z.optional(BlockingReason$inboundSchema),
    can_delete: z.boolean(),
    ema_binding_count: z.int(),
    mcp_server_names: z.array(z.string()),
    session_count: z.int(),
    trusted_user_session_issuers: z.array(
      TrustedClientUserSessionIssuerReference$inboundSchema,
    ),
  }),
  z.transform((v) => {
    return remap$(v, {
      "blocking_reason": "blockingReason",
      "can_delete": "canDelete",
      "ema_binding_count": "emaBindingCount",
      "mcp_server_names": "mcpServerNames",
      "session_count": "sessionCount",
      "trusted_user_session_issuers": "trustedUserSessionIssuers",
    });
  }),
);

export function organizationClientDeletePreflightFromJSON(
  jsonString: string,
): SafeParseResult<OrganizationClientDeletePreflight, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => OrganizationClientDeletePreflight$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'OrganizationClientDeletePreflight' from JSON`,
  );
}
